// Inline SVG icons for the primary sidebar nav — replaces the placeholder Unicode
// box glyphs (▦ ▧ ▤ ≣ ◷ ⚙ ★) that rendered at inconsistent weights/baselines across
// browsers (UX audit P1-2). Same loose, round-stroke language as the onboarding
// empty-state icons (onboarding/icons.tsx) and the media-kind glyphs
// (MediaThumb.tsx), just sized for repeated small use in the nav rail.
// currentColor throughout — .nav-link / .nav-link.active own the tone.

import type { SVGProps } from 'react';

const base: SVGProps<SVGSVGElement> = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

/** Four tiles at a glance — the dashboard's "fleet at a glance" summary. */
export function DashboardIcon() {
  return (
    <svg {...base}>
      <rect x="3" y="3.5" width="8" height="7" rx="1.4" />
      <rect x="13" y="3.5" width="8" height="4.5" rx="1.4" opacity="0.55" />
      <rect x="3" y="13" width="8" height="7.5" rx="1.4" opacity="0.55" />
      <rect x="13" y="10.5" width="8" height="10" rx="1.4" />
    </svg>
  );
}

/** A screen on a stand — mirrors the pairing icon's shape language. */
export function ScreensIcon() {
  return (
    <svg {...base}>
      <rect x="3" y="4" width="18" height="12.5" rx="2" />
      <path d="M9 20.5h6M12 16.5v4" />
    </svg>
  );
}

/** A framed photo with a mountain fold — same glyph as the media kind icon. */
export function MediaIcon() {
  return (
    <svg {...base}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="M4 16.5l4.5-4 3.5 3 3-2.5 4 3.5" opacity="0.7" />
    </svg>
  );
}

/** A stacked list feeding a play mark — an ordered loop of media. */
export function PlaylistsIcon() {
  return (
    <svg {...base}>
      <path d="M3.3 6.5h11.5M3.3 12h11.5M3.3 17.5h6.5" />
      <circle cx="17.7" cy="16" r="4.3" opacity="0.9" />
      <path d="M16.2 14.1l3 1.9-3 1.9z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** A calendar page with a clock — when and where a playlist plays. */
export function SchedulesIcon() {
  return (
    <svg {...base}>
      <rect x="3" y="5" width="11" height="11.5" rx="2" opacity="0.6" />
      <path d="M6.5 5V3.2M11.5 5V3.2M3 8.5h11" opacity="0.6" />
      <circle cx="16.7" cy="14" r="5.3" />
      <path d="M16.7 11v3l2.3 1.4" />
    </svg>
  );
}

/** A loose gear — asymmetric tooth spacing keeps it in the hand-drawn family
 *  rather than reading as a mechanical, perfectly-radial icon-font glyph. */
export function SettingsIcon() {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="3.3" />
      <path
        d="M12 3.7v2.5M18.5 6.2l-1.8 1.9M20.6 12.3H18M17 18.6l-1.7-1.8M12 20.6v-2.5M6.5 18.1l1.7-1.8M3.4 12h2.6M7 6.6l1.7 1.8"
        opacity="0.85"
      />
    </svg>
  );
}

/** Rising bars with a baseline — playback + uptime trends at a glance. */
export function AnalyticsIcon() {
  return (
    <svg {...base}>
      <path d="M3.5 3.5v17h17" />
      <rect x="7" y="12" width="2.8" height="5.5" rx="0.8" />
      <rect x="12" y="8.5" width="2.8" height="9" rx="0.8" opacity="0.75" />
      <rect x="17" y="5.5" width="2.8" height="12" rx="0.8" opacity="0.55" />
    </svg>
  );
}

/** A shield with a checkmark — superadmin trust/oversight, not a decoration. */
export function AdminIcon() {
  return (
    <svg {...base}>
      <path d="M12 3.3l7 2.9v5.4c0 4.7-3 7.9-7 9-4-1.1-7-4.3-7-9V6.2z" />
      <path d="M8.7 12.2l2.2 2.2 4.4-4.7" opacity="0.85" />
    </svg>
  );
}
